<template>
  <layout width="428">
    <template slot="header"> {{ isNew ? 'Add new' : 'Edit' }} user</template>
    <validation-observer ref="form">
      <v-form class="mx-4" :disabled="isLoading">
        <subheader>General</subheader>
        <validation-provider
          v-slot="{ errors }"
          name="Type"
          v-if="isNew"
          rules="required"
        >
          <v-select
            :menu-props="{ bottom: true, offsetY: true }"
            data-test-field="userType"
            v-model="user.type"
            autocomplete="false"
            :items="types"
            item-text="name"
            item-value="id"
            label="Type"
            :error-messages="errors"
            class="ma-0"
          >
            <template v-slot:item="{ item }">
              <div :data-test-field-type="item.name">
                {{ item.name }}
              </div>
            </template>
          </v-select>
        </validation-provider>
        <validation-provider v-slot="{ errors }" name="Login" rules="required">
          <v-text-field
            data-test-field="login"
            v-model="user.login"
            label="Login"
            autocomplete="chrome-off"
            :disabled="!isNew"
            name="not-password-hack-2"
            :error-messages="errors"
            clearable
          ></v-text-field>
        </validation-provider>
        <validation-provider
          v-if="isNew"
          v-slot="{ errors }"
          name="Password"
          :debounce="500"
          vid="password"
          rules="required|checkPassword"
        >
          <password-field
            data-test-field="newPassword"
            v-model="user.password"
            label="New password"
            autocomplete="new-password"
            name="not-password-hack"
            :error-messages="errors"
            clearable
          >
          </password-field>
        </validation-provider>
        <validation-provider
          v-if="isNew"
          v-slot="{ errors }"
          vid="repeatPassword"
          name="Repeat password"
          rules="required|confirmed:password"
        >
          <password-field
            data-test-field="repeatPassword"
            v-model="user.password2"
            label="Repeat password"
            autocomplete="new-password"
            :error-messages="errors"
            clearable
          >
          </password-field>
        </validation-provider>
        <v-select
          :menu-props="{ bottom: true, offsetY: true }"
          data-test-field="2fa"
          v-model="user.twoFaType"
          label="2FA type"
          item-text="name"
          clearable
          item-value="id"
          :items="[
            { name: 'Email', id: 'EMAIL' },
            { name: 'SMS', id: 'SMS' },
            { name: 'None', id: 'NONE' }
          ]"
          class="ma-0"
        >
          <template v-slot:item="{ item }">
            <div :data-test-2fa-types="item.name">
              {{ item.name }}
            </div>
          </template>
        </v-select>
        <div class="d-flex align-center mx-0">
          <div style="font-size: 16px">Enabled</div>
          <v-spacer />
          <v-switch
            class="ma-0 py-3"
            data-test-switch="enabled"
            hide-details
            v-model="user.enabled"
          ></v-switch>
        </div>
        <div class="d-flex align-center mx-0" v-if="currentUser.um">
          <div style="font-size: 16px">User management</div>
          <v-spacer />
          <v-switch
            data-test-switch="um"
            class="ma-0 py-3"
            hide-details
            v-model="user.um"
          ></v-switch>
        </div>
        <div class="d-flex align-center mx-0 pb-2">
          <div style="font-size: 16px">Show hidden</div>
          <v-spacer />
          <v-switch
            data-test-switch="hidden"
            class="ma-0 py-3"
            hide-details
            v-model="user.showhidden"
          ></v-switch>
        </div>
        <select-chips
          name="usersToGroups"
          @drop-item="handleDropChip(user.usersToGroups, $event, 'id')"
          v-model="user.usersToGroups"
          multiple
          type="select"
          data-test-field="userGroups"
          label="User groups"
          item-text="name"
          item-value="id"
          :items="filteredGroups"
          chips
          return-object
          class="ma-0"
        />
        <subheader>Security</subheader>
        <validation-provider
          v-slot="{ errors }"
          name="tokenExp"
          vid="tokenExp"
          rules="minSession"
        >
          <v-text-field
            min="1"
            data-test-field="tokenExp"
            v-model.number="user.tokenExp"
            label="Session duration (days)"
            type="number"
            :error-messages="errors"
            clearable
          ></v-text-field>
        </validation-provider>
        <subheader>Default groups</subheader>
        <v-select
          :menu-props="{ bottom: true, offsetY: true }"
          data-test-field="defaultGroups"
          v-model="user.defaultEditorgroup"
          label="Default editors group"
          item-text="name"
          clearable
          item-value="id"
          :items="filteredGroups"
          class="ma-0"
        >
          <template v-slot:item="{ item }">
            <div :data-test-default-groups-value="item.name">
              {{ item.name }}
            </div>
          </template>
        </v-select>
        <v-select
          :menu-props="{ bottom: true, offsetY: true }"
          data-test-field="defaultUsersGroup"
          v-model="user.defaultUsergroup"
          label="Default users group"
          item-text="groupName"
          item-value="id"
          :items="groups"
          class="ma-0"
        >
          <template v-slot:item="{ item }">
            <div :data-default-user-groups-value="item.groupName">
              {{ item.groupName }}
            </div>
          </template>
        </v-select>
        <validation-provider v-slot="{ errors }" name="Field" rules="required">
          <v-select
            :menu-props="{ bottom: true, offsetY: true }"
            v-model="user.defaultReadergroup"
            label="Default readers group"
            disabled
            item-text="groupName"
            :error-messages="errors"
            item-value="id"
            :items="groups"
            class="ma-0"
          ></v-select>
        </validation-provider>
        <subheader>Contact info</subheader>
        <v-text-field
          data-test-field="mName"
          v-model="user.mName"
          label="Name"
          clearable
        ></v-text-field>
        <validation-provider
          v-slot="{ errors }"
          name="Email"
          rules="email"
          :skip-if-empty="true"
        >
          <v-text-field
            data-test-field="mEmail"
            autocomplete="false"
            v-model="user.mEmail"
            :error-messages="errors"
            label="Email"
            clearable
          ></v-text-field>
        </validation-provider>
        <v-text-field
          data-test-field="mPhone"
          autocomplete="false"
          v-model="user.mPhone"
          label="Phone number"
          clearable
        ></v-text-field>
        <v-text-field
          autocomplete="false"
          data-test-field="mExternalId"
          v-model="user.mExternalId"
          label="Second ID"
          clearable
        ></v-text-field>
        <select-chips
          name="mTags"
          data-test-field="mTags"
          @drop-item="handleDropChip(user.mTags, $event, 'string')"
          v-model="user.mTags"
          multiple
          label="Tags"
          item-text="name"
          item-value="id"
          chips
          class="ma-0"
        />
        <subheader>Photo</subheader>
        <media-input
          data-test-field="mPicture"
          name="mPicture"
          v-model="mPictureFile"
          :id="user.mPicture"
          rules="image|size:20"
        />
        <subheader>Icon</subheader>
        <media-input
          v-model="mIconFile"
          :id="user.mIcon"
          name="mIcon"
          rules="image|size:20"
        />
        <subheader>Description</subheader>
        <v-textarea
          data-test-field="description"
          data-test="description"
          v-model="user.description"
          no-resize
          rows="1"
          auto-grow
        ></v-textarea>
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close" data-test-action="userCloseAction">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="userCreateOrEditAction"
        :loading="isLoading"
        text
        @click.stop="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import SelectChips from '@/components/SelectChips';

import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs
} from '@vue/composition-api';

import { required, email, confirmed, min } from 'vee-validate/dist/rules';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';

extend('required', {
  ...required,
  message: 'This field is required'
});

extend('min', {
  ...min,
  message: 'Password must contain at least 8 characters'
});
extend('minSession', {
  validate(value) {
    return value >= 1;
  },
  params: ['min'],
  message: 'The value must be at least 1'
});

extend('email', {
  ...email,
  message: 'Invalid email address'
});

extend('confirmed', {
  ...confirmed,
  message: 'Passwords must be the same'
});

import useAuth from '@/api/auth';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useResult
} from '@vue/apollo-composable';

import MediaInput from '@/components/MediaInput';
import useMedia from '@/compositions/useMedia';
import userSchema from '@/api/users/item.graphql';
import userSchemas from '@/api/users/list.graphql';

import differenceBy from 'lodash.differenceby';
import PasswordField from '@/components/app-entity/modals/PasswordField';

export default {
  name: 'CreateUser',
  props: {
    entity: {
      type: [Object],
      required: true
    },
    linkAfterCreation: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    callbackAfter: {
      type: Function,
      default: () => {}
    }
  },
  components: {
    PasswordField,
    MediaInput,
    Layout,
    SelectChips,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit, root, refs }) {
    const { resolveClient } = useApolloClient();
    const client = resolveClient();

    extend('checkPassword', {
      validate: async value => {
        const { data } = await client.query({
          query: userSchema.validatePasswordChange,
          variables: {
            userId: null,
            newPassword: value
          }
        });

        const response = JSON.parse(data.validatePasswordChange);

        if (response.valid) {
          return true;
        }

        return response.comment;
      }
    });

    const controller = new window.AbortController();
    const { user, loadUser } = new useAuth();
    const mediaComposition = useMedia();
    const { mutate: updateUser } = useMutation(userSchema.update);
    const { mutate: createUser } = useMutation(userSchemas.save);
    const { mutate: addToGroup } = useMutation(userSchemas.addToGroup);
    const { mutate: deleteUsersToGroup } = useMutation(
      userSchema.deleteUsersGroup
    );

    const form = ref(null);

    const localState = reactive({
      loadFile: false,
      mPictureFile: 0,
      mPictureFileProgress: 0,
      mIconFile: 0,
      mIconFileProgress: 0,
      user: {
        id: null,
        login: null,
        type: 'USER',
        description: null,
        enabled: true,
        showhidden: true,
        password: null,
        password2: null,
        mEmail: null,
        mExternalId: null,
        mName: null,
        um: false,
        mPhone: null,
        mTags: null,
        defaultEditorgroup: null,
        defaultReadergroup: 'ffffffff-ffff-ffff-ffff-ffffffffffff',
        defaultUsergroup: 'ffffffff-ffff-ffff-ffff-ffffffffffff',
        mPicture: null,
        mIcon: null,
        userGroups: [],
        usersToGroups: [],
        tokenExp: 1,
        twoFaType: 'NONE'
      },
      options: {
        bgColor: 'transparent',
        textColor: 'white',
        showTitle: false,
        shadow: true
      }
    });

    const lazyQueryGroups = useLazyQuery(userSchemas.loadGroups, null, {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only'
    });

    const groups = useResult(lazyQueryGroups.result, [], data => {
      return data.userGroups;
    });

    const filteredGroups = computed(() => {
      return groups.value
        .filter(item => !item.isSystem)
        .map(item => ({ name: item.groupName, id: item.id }));
    });

    const types = ref([
      { id: 'USER', name: 'Person' },
      { id: 'APP', name: 'Application' }
    ]);

    const isNew = computed(() => {
      return !props.entity.id;
    });

    function close() {
      emit('close');
    }

    const createObjectHandler = () => {
      uploadFiles().then(files => {
        const mPicture = files.find(el => el.type === 'picture')?.id;
        const mIcon = files.find(el => el.type === 'icon')?.id;

        createUser({
          input: {
            user: {
              editorgroup: user.value.editorgroup,
              type: localState.user.type,
              login: localState.user.login,
              description: localState.user.description,
              enabled: localState.user.enabled,
              showhidden: localState.user.showhidden,
              password: localState.user.password,
              mEmail: localState.user.mEmail,
              mExternalId: localState.user.mExternalId,
              mName: localState.user.mName,
              um: localState.user.um,
              mPhone: localState.user.mPhone,
              mTags: localState.user.mTags,
              tokenExp: localState.user.tokenExp || 1,
              defaultEditorgroup: localState.user.defaultEditorgroup,
              defaultUsergroup: localState.user.defaultUsergroup,
              mPicture: mPicture || localState.user.mPicture,
              twoFaType: localState.user.twoFaType || 'NONE',
              mIcon: mIcon || localState.user.mIcon
            }
          }
        })
          .then(({ data }) => {
            localState.user.id = data.createUser.user.id;
            const promises = [];
            localState.user.usersToGroups.forEach(i => {
              promises.push(
                addToGroup({
                  input: {
                    usersToGroup: {
                      userId: localState.user.id,
                      userGroupId: i.id
                    }
                  }
                })
              );
            });

            return Promise.all(promises);
          })
          .then(() => {
            redirectToCard(localState.user.id);
            close();
          });
      });
    };

    const uploadFiles = () => {
      const promises = [];

      if (localState.mPictureFile) {
        promises.push(
          mediaComposition
            .uploadMedia(
              localState.mPictureFile,
              localState.mPictureFileProgress
            )
            .then(({ data }) =>
              Promise.resolve({
                type: 'picture',
                id: data
              })
            )
        );
      } else {
        if (localState.mPictureFile === null) {
          localState.user.mPicture = null;
        }

        if (localState.mPictureFile === 0) {
          localState.user.mPicture = props.entity.mPicture;
        }
      }

      if (localState.mIconFile) {
        promises.push(
          mediaComposition
            .uploadMedia(localState.mIconFile, localState.mIconFileProgress)
            .then(({ data }) =>
              Promise.resolve({
                type: 'icon',
                id: data
              })
            )
        );
      } else {
        if (localState.mIconFile === null) {
          localState.user.mIcon = null;
        }

        if (localState.mIconFile === 0) {
          localState.user.mIcon = props.entity.mIcon;
        }
      }

      return Promise.all(promises);
    };

    const updateObjectHandler = () => {
      uploadFiles().then(files => {
        const mPicture = files.find(el => el.type === 'picture')?.id;
        const mIcon = files.find(el => el.type === 'icon')?.id;

        updateUser({
          input: {
            id: props.entity.id,
            patch: {
              description: localState.user.description,
              enabled: localState.user.enabled,
              mEmail: localState.user.mEmail,
              mExternalId: localState.user.mExternalId,
              showhidden: localState.user.showhidden,
              mName: localState.user.mName,
              um: localState.user.um,
              mPhone: localState.user.mPhone,
              mTags: localState.user.mTags,
              defaultEditorgroup: localState.user.defaultEditorgroup,
              defaultUsergroup: localState.user.defaultUsergroup,
              mPicture: mPicture || localState.user.mPicture,
              twoFaType: localState.user.twoFaType || 'NONE',
              mIcon: mIcon || localState.user.mIcon,
              tokenExp: localState.user.tokenExp || 1
            }
          }
        })
          .then(() => {
            const promises = [];
            groupsToBeAdded().forEach(i => {
              promises.push(
                addToGroup({
                  input: {
                    usersToGroup: {
                      userId: props.entity.id,
                      userGroupId: i.userGroup
                    }
                  }
                })
              );
            });
            groupsToBeRemoved().forEach(i => {
              promises.push(
                deleteUsersToGroup({
                  input: {
                    id: i.connectId
                  }
                })
              );
            });
            return Promise.all(promises);
          })
          .then(() => {
            if (user.value.id === props.entity.id) {
              loadUser();
            } else {
              close();
            }
          })
          .then(() => close());
      });
    };

    const groupsToBeRemoved = () => {
      let needRemove = [];
      if (props.entity?.id) {
        const oldGroups = props.entity.usersToGroups.map(item => ({
          userGroup: item.userGroup.id,
          connectId: item.id
        }));

        const newGroups = localState.user.usersToGroups.map(item => ({
          userGroup: item.id,
          connectId: item.connectId
        }));

        needRemove = differenceBy(oldGroups, newGroups, 'userGroup');
      }
      return needRemove;
    };

    const groupsToBeAdded = () => {
      let diff = [];
      if (props.entity?.id) {
        const oldGroups = props.entity.usersToGroups.map(item => ({
          userGroup: item.userGroup.id
        }));

        const newGroups = localState.user.usersToGroups.map(item => ({
          userGroup: item.id
        }));

        diff = differenceBy(newGroups, oldGroups, 'userGroup');
      }
      return diff;
    };

    const redirectToCard = userId => {
      root.$router.push({
        name: 'usersTableComponent',
        params: { id: userId }
      });
    };

    onMounted(() => {
      lazyQueryGroups.load();
      if (props.entity?.id) {
        let groups = props.entity.usersToGroups.map(item => ({
          id: item.userGroup.id,
          connectId: item.id,
          name: item.userGroup.groupName
        }));

        localState.user.usersToGroups.push(...groups);
        localState.user.editorgroup = props.entity.editorgroup;
        localState.user.login = props.entity.login;
        localState.user.type = props.entity.type;
        localState.user.description = props.entity.description;
        localState.user.enabled = props.entity.enabled;
        localState.user.um = props.entity.um;
        localState.user.showhidden = props.entity.showhidden;
        localState.user.password = props.entity.password;
        localState.user.mEmail = props.entity.mEmail;
        localState.user.mExternalId = props.entity.mExternalId;
        localState.user.mName = props.entity.mName;
        localState.user.mPhone = props.entity.mPhone;
        localState.user.defaultEditorgroup = props.entity.defaultEditorgroup;
        localState.user.defaultReadergroup = props.entity.defaultReadergroup;
        localState.user.defaultUsergroup = props.entity.defaultUsergroup;
        localState.user.mPicture = props.entity.mPicture;
        localState.user.mIcon = props.entity.mIcon;
        localState.user.twoFaType = props.entity.twoFaType || 'NONE';
        localState.user.mTags = props.entity.mTags || [];
        localState.user.tokenExp = props.entity.tokenExp;
      }
    });

    const handleDropChip = (array, id, typeOperation) => {
      if (typeOperation === 'id') {
        array.splice(
          array.findIndex(i => i.id === id),
          1
        );
      } else {
        array.splice(
          array.findIndex(i => i === id),
          1
        );
      }
    };

    const handleSave = () => {
      form.value.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createObjectHandler();
        } else {
          updateObjectHandler();
        }
      });
    };

    return {
      ...toRefs(localState),
      groups,
      isNew,
      close,
      types,
      handleSave,
      filteredGroups,
      isLoading: false,
      handleDropChip,
      form,
      currentUser: user
    };
  }
};
</script>
